import React, { useMemo, useState } from 'react';
import { createProjectColumn, pasteProjectTasks, errorMessage } from '../../services/api';
import { useLanguage } from '../../i18n/LanguageContext';
import { parseCsv } from '../../utils/csv';
import { autoMap, importableColumns } from '../../utils/projectCsv';
import Select from '../Select';

const CHUNK = 200;

/**
 * Importa un CSV nel progetto: si abbina ogni colonna del file a una colonna
 * del progetto (o a una nuova colonna di testo, o si ignora), si vede
 * un'anteprima e si importa a blocchi. Le opzioni mancanti nelle colonne di
 * selezione possono essere create al volo.
 */
function ImportCsvDialog({ projectId, columns, text, fileName, onClose, onDone }) {
  const { t } = useLanguage();
  const rows = useMemo(() => parseCsv(text), [text]);
  const [hasHeader, setHasHeader] = useState(true);
  const headers = useMemo(() => (hasHeader ? rows[0] || [] : (rows[0] || []).map((_, i) => `${t('projectCsv.column')} ${i + 1}`)), [rows, hasHeader, t]);
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const [mapping, setMapping] = useState(() => autoMap(rows[0] || [], columns));
  const [createOptions, setCreateOptions] = useState(true);
  const [progress, setProgress] = useState(null); // {done, total}
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  const usable = importableColumns(columns);
  const used = mapping.filter(Boolean);

  const run = async () => {
    setError('');
    setProgress({ done: 0, total: dataRows.length });
    try {
      // Colonne nuove (di testo) per le intestazioni scelte come "nuova colonna".
      const keys = [...mapping];
      for (let i = 0; i < keys.length; i += 1) {
        if (keys[i] === 'new') {
          const data = await createProjectColumn(projectId, { name: (headers[i] || `${t('projectCsv.column')} ${i + 1}`).trim().slice(0, 100), type: 'text' });
          keys[i] = `col:${data.column.id}`;
        }
      }
      let imported = 0;
      for (let start = 0; start < dataRows.length; start += CHUNK) {
        const chunk = dataRows.slice(start, start + CHUNK).map((row) => {
          const cells = {};
          keys.forEach((key, i) => {
            if (!key) return;
            cells[key === 'title' ? 'title' : key.slice(4)] = row[i] ?? '';
          });
          return { cells };
        });
        const data = await pasteProjectTasks(projectId, chunk, createOptions);
        imported += data.tasks.length;
        setProgress({ done: Math.min(start + CHUNK, dataRows.length), total: dataRows.length });
      }
      setSummary(imported);
      onDone();
    } catch (err) {
      setError(errorMessage(err, t('projectCsv.importError')));
      setProgress(null);
    }
  };

  return (
    <>
      <div className="pd-modal-overlay" onClick={progress ? undefined : onClose} />
      <div className="pd-modal pd-import" role="dialog" aria-label={t('projectCsv.importTitle')}>
        <h3>{t('projectCsv.importTitle')}</h3>
        <p className="nt-muted">{fileName} · {t('projectCsv.rowsFound', { count: dataRows.length })}</p>

        {summary !== null ? (
          <>
            <p>{t('projectCsv.imported', { count: summary })}</p>
            <div className="pd-popover-actions"><button type="button" className="btn" onClick={onClose}>{t('projectDetail.close')}</button></div>
          </>
        ) : (
          <>
            <label className="pd-check-line">
              <input type="checkbox" checked={hasHeader} onChange={(e) => { setHasHeader(e.target.checked); setMapping(autoMap(e.target.checked ? rows[0] || [] : (rows[0] || []).map(() => ''), columns)); }} />
              {t('projectCsv.firstRowHeader')}
            </label>
            <div className="pd-import-map">
              {headers.map((header, i) => (
                <div key={i} className="pd-import-row">
                  <span className="pd-import-header" title={header}>{header || `${t('projectCsv.column')} ${i + 1}`}</span>
                  <span aria-hidden="true">→</span>
                  <Select value={mapping[i] || ''} aria-label={header} onChange={(e) => setMapping(mapping.map((m, k) => (k === i ? e.target.value : m)))}>
                    <option value="">{t('projectCsv.ignore')}</option>
                    {usable.map((c) => {
                      const key = c.type === 'title' ? 'title' : `col:${c.id}`;
                      return <option key={key} value={key} disabled={used.includes(key) && mapping[i] !== key}>{c.name}</option>;
                    })}
                    <option value="new">{t('projectCsv.newColumn')}</option>
                  </Select>
                  <span className="nt-muted pd-import-sample">{(dataRows[0] || [])[i]}</span>
                </div>
              ))}
            </div>
            <label className="pd-check-line">
              <input type="checkbox" checked={createOptions} onChange={(e) => setCreateOptions(e.target.checked)} />
              {t('projectCsv.createOptions')}
            </label>
            {error && <div className="error-message">{error}</div>}
            {progress && <p className="nt-muted">{t('projectCsv.progress', { done: progress.done, total: progress.total })}</p>}
            <div className="pd-popover-actions">
              <button type="button" className="btn" disabled={Boolean(progress) || dataRows.length === 0 || !mapping.some(Boolean)} onClick={run}>
                {t('projectCsv.import', { count: dataRows.length })}
              </button>
              <button type="button" className="btn btn-secondary" disabled={Boolean(progress)} onClick={onClose}>{t('common.cancel')}</button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ImportCsvDialog;
